import { useState } from 'react';

import { GroupedInput, InputAddon, InputGroup, TextField } from '../../../components/ui/Field';
import { Section } from '../../../components/ui/Panel';

/**
 * SMTP connection details.
 *
 * The password field carries the most subtlety on this page. Two things are going on:
 *
 *   - It is never restored from browser storage. The plaintext lives encrypted on the
 *     server, and an empty field is how the form asks /send-email to use that stored
 *     credential. The hint says so, because an empty password box on a send form
 *     otherwise looks like an omission.
 *
 *   - `autoComplete="new-password"` here and `"off"` on the user field stop the
 *     browser's password manager treating the pair as a login form. Without them
 *     Chromium fires a trusted input event on load and injects a generated password,
 *     which was then saved as the operator's SMTP credential and submitted — so sending
 *     failed authentication with a password nobody had typed. The gesture tracking in
 *     useCampaignForm is the second half of that defence.
 */
export function SmtpCredentialsCard({
  form,
  setField,
  fieldErrors,
  hasSavedPassword,
  passwordIntent,
  onPasswordIntent,
  onPasswordChange,
  disabled
}) {
  const fieldError = (field) => fieldErrors?.[field];
  const [revealed, setRevealed] = useState(false);

  const passwordHint = hasSavedPassword
    ? passwordIntent
      ? 'This will replace the password stored on the server.'
      : 'A saved password is stored securely on the server and will be used when you send. Type here to replace it.'
    : 'Stored encrypted on the server as you type, so it is not kept in this browser.';

  const hasError = ['smtpHost', 'smtpPort', 'smtpUser', 'smtpPass'].some((field) => fieldError(field));
  const summary = form.smtpHost
    ? `${form.smtpUser ? `${form.smtpUser} @ ` : ''}${form.smtpHost}${form.smtpPort ? `:${form.smtpPort}` : ''}`
    : 'Not configured';

  return (
    <Section
      title="SMTP Server"
      icon="fa-server"
      collapsible
      storageKey="opterite.dashboard.section.smtp"
      // A field the send just rejected must be on screen.
      forceOpen={hasError}
      summary={summary}
    >
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_5.5rem] gap-2">
        <TextField
          id="smtp-host"
          label="SMTP Hostname or IP"
          required
          placeholder="smtp.example.com"
          autoComplete="off"
          value={form.smtpHost}
          onChange={(event) => setField('smtpHost', event.target.value)}
          disabled={disabled}
          error={fieldError('smtpHost')}
        />

        <TextField
          id="smtp-port"
          label="Port"
          required
          placeholder="587"
          inputMode="numeric"
          autoComplete="off"
          value={form.smtpPort}
          onChange={(event) => setField('smtpPort', event.target.value)}
          disabled={disabled}
          title="465 is treated as implicit TLS; anything else uses STARTTLS."
          error={fieldError('smtpPort')}
        />
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
        <TextField
          id="smtp-user"
          label="SMTP User"
          required
          placeholder="Enter SMTP User"
          autoComplete="off"
          value={form.smtpUser}
          onChange={(event) => setField('smtpUser', event.target.value)}
          disabled={disabled}
          error={fieldError('smtpUser')}
        />

        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor="smtp-pass" className="text-xs font-semibold text-ink-700">
            SMTP Password
            {hasSavedPassword ? null : (
              <span className="ml-0.5 text-danger-500" aria-hidden="true">*</span>
            )}
          </label>

          <InputGroup invalid={Boolean(fieldError('smtpPass'))}>
            <GroupedInput
              id="smtp-pass"
              type={revealed ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={hasSavedPassword && !passwordIntent ? 'Saved password in use' : 'Enter SMTP Password'}
              value={form.smtpPass}
              disabled={disabled}
              aria-invalid={fieldError('smtpPass') ? true : undefined}
              aria-describedby="smtp-pass-hint"
              // The three events a human produces and the browser's autofill does not.
              // Until one of them fires, changes to this field are ignored.
              onKeyDown={onPasswordIntent}
              onPaste={onPasswordIntent}
              onPointerDown={onPasswordIntent}
              onChange={(event) => onPasswordChange(event.target.value)}
            />
            <InputAddon
              onClick={() => setRevealed((current) => !current)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              title={revealed ? 'Hide password' : 'Show password'}
            >
              <i className={revealed ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'} aria-hidden="true" />
            </InputAddon>
          </InputGroup>
        </div>
      </div>

      {fieldError('smtpPass') ? (
        <span className="text-xs text-danger-600" role="alert">
          {fieldError('smtpPass')}
        </span>
      ) : (
        <span id="smtp-pass-hint" className="text-xs leading-tight text-muted" role="status" aria-live="polite">
          {hasSavedPassword ? (
            <i className="fa-solid fa-lock mr-1 text-success-600" aria-hidden="true" />
          ) : null}
          {passwordHint} Port 465 uses implicit TLS; others use STARTTLS.
        </span>
      )}
    </Section>
  );
}
